import {
  BaseUserLimitCard,
  UserAvatarDisplay,
  LimitDisplay,
  UserLimitActions,
  type UserLimitField,
  type UserLimitItem,
} from '../components'
import {
  useGetOrgUsersSpendingLimit,
  useCreateOrgUserSpendingLimit,
  useResetOrgUserSpendingLimit,
} from '@/services/settings'
import { useGetOrgUsers } from '@/services/user-management'
import { z } from 'zod'
import { SpendingLimit, WebrpcError } from '@/services/inference.gen'
import { TableColumn } from '@/components/data-table/DataTable'
import { centsToDollars, dollarsToCents } from '@/utils/money'

type UserCreditLimitCardProps = {
  orgCreditLimit: number
}

// Form schemas
const editLimitFormSchema = z.object({
  creditLimit: z.number().min(0),
})

const addLimitFormSchema = z.object({
  userId: z.string().min(1, 'Please select a user'),
  creditLimit: z.number().min(0),
})

type CreditLimitUserItem = UserLimitItem & {
  limit: number
}

export const UserCreditLimitCard = ({
  orgCreditLimit,
}: UserCreditLimitCardProps) => {
  // Fetch spending limits
  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchOrgUsersSpendingLimits,
  } = useGetOrgUsersSpendingLimit(0)

  // Fetch all org users for the dropdown
  const { data: orgUsersData } = useGetOrgUsers('active', '', undefined)

  const updateMutation = useCreateOrgUserSpendingLimit()
  const resetMutation = useResetOrgUserSpendingLimit()

  // Transform data to match UserLimitItem interface
  const transformedData: CreditLimitUserItem[] = (
    data?.spending_limits || []
  ).map((item: SpendingLimit) => {
    const userFromOrgData = orgUsersData?.allUsers.find(
      (orgUser) => orgUser.user_id === item.user?.user_id
    )

    return {
      user_id: item.user?.user_id || 0,
      email: item.user?.email || '',
      first_name:
        userFromOrgData?.profile_metadata?.first_name ||
        item.user_organization?.profile_metadata?.first_name,
      last_name:
        userFromOrgData?.profile_metadata?.last_name ||
        item.user_organization?.profile_metadata?.last_name,
      avatar_url:
        userFromOrgData?.profile_metadata?.avatar_preview_url ||
        item.user_organization?.profile_metadata?.avatar_preview_url,
      limit: centsToDollars(item.max_spend_cent),
    }
  })

  const limitFields: UserLimitField[] = [
    {
      name: 'creditLimit',
      label: 'Credit Limit',
      placeholder: 'Enter credit limit',
      testId: 'credit-limit-input',
    },
  ]

  const tableColumns = (
    handleEditClick: (user: any) => void,
    handleResetClick: (user: any) => void
  ): TableColumn<CreditLimitUserItem>[] => [
    {
      id: 'user',
      header: 'User',
      width: 'w-1/2',
      cell: (item) => (
        <UserAvatarDisplay
          avatarUrl={item.avatar_url}
          firstName={item.first_name}
          lastName={item.last_name}
          email={item.email}
        />
      ),
    },
    {
      id: 'limit',
      header: 'Credit Limit',
      width: 'w-1/4',
      cell: (item) => <LimitDisplay value={`$${item.limit}`} unit="" />,
    },
    {
      id: 'actions',
      header: '',
      width: 'w-1/4',
      cell: (item) => (
        <UserLimitActions
          onEdit={() =>
            handleEditClick({
              id: item.user_id,
              email: item.email,
              creditLimit: item.limit,
            })
          }
          onReset={() =>
            handleResetClick({
              id: item.user_id,
              email: item.email,
              creditLimit: item.limit,
            })
          }
          testIds={{
            editButton: 'edit-user-limit-button',
            resetButton: 'reset-user-limit-button',
          }}
        />
      ),
    },
  ]

  const dialogConfig = {
    editTitle: 'Edit User Credit Limit',
    addTitle: 'Add User Credit Limit',
    editDescription: 'Set a custom credit limit for this user.',
    addDescription: 'Set a custom credit limit for a user.',
    confirmTitle: 'Confirm User Credit Limit Override',
    confirmDescription: (user: any) =>
      `You're about to set a $${user.creditLimit} credit limit for ${user.email}.`,
    resetTitle: 'Reset User Credit Limit to Default',
    resetDescription: (user: any, defaultLimits: Record<string, number>) =>
      `You're about to reset ${user.email}'s credit limit to the organization default ($${defaultLimits.creditLimit}).`,
    confirmPrompt: (user: any) => `${user.creditLimit}`,
    warningPoints: [
      "This user's credit limit will be set independently from the organization-wide limit",
      'Some running resources may be affected if the limit is lowered',
    ],
    resetWarning:
      'This will remove any custom credit limit for this user. They will follow the organization-wide default limit.',
  }

  const testIds = {
    addButton: 'add-user-credit-limit-button',
    userSelectTrigger: 'user-select-trigger',
    editButton: 'edit-user-limit-button',
    resetButton: 'reset-user-limit-button',
    cancelButton: 'cancel-user-limit-button',
    continueButton: 'continue-user-limit-button',
  }

  // Custom functions for the base component
  const extractUserLimits = (item: any) => ({
    creditLimit: item.creditLimit,
  })

  const createMutationData = (
    userId: number,
    limits: Record<string, number>
  ) => ({
    cent: dollarsToCents(limits.creditLimit),
    user_id: userId,
  })

  const resetMutationData = (userId: number) => ({
    user_id: userId,
  })

  const wrappedUpdateMutation = {
    mutate: (data: any, options?: any) => {
      updateMutation.mutate(data, {
        ...options,
        onSuccess: () => {
          refetchOrgUsersSpendingLimits()
          options?.onSuccess?.()
        },
        onError: (error: any) => {
          if (error instanceof WebrpcError) {
            options?.onError?.(JSON.stringify(error))
          } else {
            options?.onError?.('Failed to update credit limit')
          }
        },
      })
    },
    isPending: updateMutation.isPending,
  }

  const wrappedResetMutation = {
    mutate: (data: any, options?: any) => {
      resetMutation.mutate(data, {
        ...options,
        onSuccess: () => {
          refetchOrgUsersSpendingLimits()
          options?.onSuccess?.()
        },
        onError: (error: any) => {
          if (error instanceof WebrpcError) {
            options?.onError?.(JSON.stringify(error))
          } else {
            options?.onError?.('Failed to reset credit limit')
          }
        },
      })
    },
    isPending: resetMutation.isPending,
  }

  return (
    <BaseUserLimitCard
      data={transformedData}
      isLoading={isLoading}
      isFetching={isFetching}
      hasNextPage={data?.has_more || false}
      orgUsersData={orgUsersData}
      editFormSchema={editLimitFormSchema}
      addFormSchema={addLimitFormSchema}
      updateMutation={wrappedUpdateMutation}
      resetMutation={wrappedResetMutation}
      limitFields={limitFields}
      defaultLimits={{ creditLimit: orgCreditLimit }}
      tableColumns={tableColumns}
      dialogConfig={dialogConfig}
      testIds={testIds}
      extractUserLimits={extractUserLimits}
      createMutationData={createMutationData}
      resetMutationData={resetMutationData}
    />
  )
}
